import React, { memo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Divider,
} from '@mui/material';
import type { DailyShiftSlotGrouped } from '../../../../domain/dailyShiftSlot';
import { DailyShiftSlotPolicy } from '../../../../domain/dailyShiftSlot';

interface CopyDailySlotsDialogProps {
  open: boolean;
  sourceDate: DailyShiftSlotGrouped | null;
  days: DailyShiftSlotGrouped[];
  onClose: () => void;
  onCopy: (targetDates: string[]) => Promise<boolean>;
  isLoading: boolean;
}

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'] as const;

/**
 * 日付をラベル形式（M月D日（曜））に変換
 */
const formatDateLabel = (date: string, dayOfWeek: number): string => {
  const [, month, day] = date.split('-').map(Number);
  return `${month}月${day}日（${WEEKDAY_LABELS[dayOfWeek]}）`;
};

/**
 * 日毎シフト枠コピーダイアログコンポーネント
 *
 * - コピー元日付の募集枠・急募枠を選択した日付にコピー
 * - コピー先日付は複数選択可能（チェックボックス + 全選択/全解除）
 * - コピー元日付は一覧から除外、確定済みの日付は選択不可
 * - 診療予約枠・確定枠・不足枠は自動計算のためコピー対象外
 */
const CopyDailySlotsDialog: React.FC<CopyDailySlotsDialogProps> = memo(({
  open,
  sourceDate,
  days,
  onClose,
  onCopy,
  isLoading,
}) => {
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());

  // ダイアログクローズ時に選択状態をリセット
  useEffect(() => {
    if (!open) {
      setSelectedDates(new Set());
    }
  }, [open]);

  if (!sourceDate) {
    return null;
  }

  // コピー元日付を除いた一覧（確定済みは選択不可）
  const candidateDays = days.filter(day => day.date !== sourceDate.date);
  const selectableDays = candidateDays.filter(day => DailyShiftSlotPolicy.isEditable(day.status));

  const allSelected = selectableDays.length > 0 && selectedDates.size === selectableDays.length;
  const someSelected = selectedDates.size > 0 && selectedDates.size < selectableDays.length;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedDates(new Set());
    } else {
      setSelectedDates(new Set(selectableDays.map(day => day.date)));
    }
  };

  const handleToggle = (date: string) => {
    const newSelected = new Set(selectedDates);
    if (newSelected.has(date)) {
      newSelected.delete(date);
    } else {
      newSelected.add(date);
    }
    setSelectedDates(newSelected);
  };

  const handleSubmit = async () => {
    const success = await onCopy(Array.from(selectedDates));
    if (success) {
      onClose();
    }
  };

  const [sourceYear, sourceMonth, sourceDay] = sourceDate.date.split('-').map(Number);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>他の日付にコピー</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {sourceYear}年{sourceMonth}月{sourceDay}日の募集医師枠数・急募医師枠数を、選択した日付にコピーします。
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          ※ 募集済み枠数・診療予約枠(合計)・確定医師枠数・不足医師枠数はコピーされません。
        </Typography>

        <List sx={{ width: '100%', bgcolor: 'background.paper', maxHeight: 400, overflow: 'auto' }}>
          {/* 全選択/全解除 */}
          <ListItem disablePadding>
            <ListItemButton onClick={handleToggleAll} dense disabled={selectableDays.length === 0}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={allSelected}
                  indeterminate={someSelected}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography fontWeight={600}>
                    すべて選択
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>

          <Divider />

          {/* 日付リスト */}
          {candidateDays.map(day => {
            const isSelectable = DailyShiftSlotPolicy.isEditable(day.status);
            return (
              <ListItem key={day.date} disablePadding>
                <ListItemButton
                  onClick={() => handleToggle(day.date)}
                  dense
                  disabled={!isSelectable}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedDates.has(day.date)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={formatDateLabel(day.date, day.dayOfWeek)}
                    secondary={
                      !isSelectable
                        ? '確定済みのため選択できません'
                        : day.isHoliday && day.holidayName
                          ? `${day.holidayName}（祝日）`
                          : undefined
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={selectedDates.size === 0 || isLoading}
        >
          {isLoading ? 'コピー中...' : 'コピー実行'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

CopyDailySlotsDialog.displayName = 'CopyDailySlotsDialog';

export default CopyDailySlotsDialog;
